import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Tag, Calendar, Loader2 } from 'lucide-react';
import { promotionsAPI } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { format } from 'date-fns';

const AdminPromotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', promotion_type: 'multiplier', value: '',
    target_tiers: ['all'], start_date: '', end_date: '', is_active: true
  });

  const tiers = ['all', 'Bronze', 'Silver', 'Gold', 'Platinum'];
  const promoTypes = [
    { value: 'multiplier', label: 'Point Multiplier (e.g., 2x)' },
    { value: 'bonus', label: 'Bonus Points' },
    { value: 'discount', label: 'Discount %' }
  ];

  useEffect(() => { fetchPromotions(); }, []);

  const fetchPromotions = async () => {
    try {
      const response = await promotionsAPI.getAll();
      setPromotions(response.data.promotions || []);
    } catch (error) {
      console.error('Failed to fetch promotions:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (promo = null) => {
    if (promo) {
      setEditing(promo);
      setForm({
        name: promo.name, description: promo.description, promotion_type: promo.promotion_type,
        value: promo.value.toString(), target_tiers: promo.target_tiers,
        start_date: promo.start_date.split('T')[0], end_date: promo.end_date.split('T')[0], is_active: promo.is_active
      });
    } else {
      setEditing(null);
      const today = new Date().toISOString().split('T')[0];
      const nextMonth = new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0];
      setForm({ name: '', description: '', promotion_type: 'multiplier', value: '', target_tiers: ['all'], start_date: today, end_date: nextMonth, is_active: true });
    }
    setShowModal(true);
  };

  const handleTierToggle = (tier) => {
    setForm(prev => {
      let tiers = [...prev.target_tiers];
      if (tier === 'all') {
        tiers = tiers.includes('all') ? [] : ['all'];
      } else {
        tiers = tiers.filter(t => t !== 'all');
        if (tiers.includes(tier)) {
          tiers = tiers.filter(t => t !== tier);
        } else {
          tiers.push(tier);
        }
      }
      return { ...prev, target_tiers: tiers.length > 0 ? tiers : ['all'] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.value || !form.start_date || !form.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSaving(true);
    try {
      const data = { ...form, value: parseFloat(form.value) };
      if (editing) {
        await promotionsAPI.update(editing.id, data);
        toast.success('Promotion updated');
      } else {
        await promotionsAPI.create(data);
        toast.success('Promotion created');
      }
      fetchPromotions();
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to save promotion');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (promo) => {
    if (!window.confirm(`Delete "${promo.name}"?`)) return;
    try {
      await promotionsAPI.delete(promo.id);
      toast.success('Promotion deleted');
      fetchPromotions();
    } catch (error) {
      toast.error('Failed to delete promotion');
    }
  };

  const isActive = (promo) => {
    const now = new Date();
    return promo.is_active && new Date(promo.start_date) <= now && new Date(promo.end_date) >= now;
  };

  return (
    <div className="space-y-6 pt-12 lg:pt-0 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-[#4A154B]">Promotions</h1>
          <p className="text-gray-600">Manage special offers and point multipliers</p>
        </div>
        <Button onClick={() => openModal()} className="btn-primary" data-testid="add-promo-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Promotion
        </Button>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="skeleton h-48 rounded-2xl" />)}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {promotions.map((promo) => (
            <div key={promo.id} className={`glass rounded-2xl p-6 card-hover ${!isActive(promo) ? 'opacity-60' : ''}`} data-testid={`promo-card-${promo.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center">
                    <Tag className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-serif text-lg text-[#4A154B]">{promo.name}</h3>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${isActive(promo) ? 'bg-[#8FBC8F]/20 text-[#5a8a5a]' : 'bg-gray-200 text-gray-600'}`}>
                      {isActive(promo) ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">{promo.description}</p>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-500">Value</p>
                  <p className="font-semibold text-[#4A154B]">
                    {promo.promotion_type === 'multiplier' ? `${promo.value}x` : promo.promotion_type === 'bonus' ? `+${promo.value} pts` : `${promo.value}%`}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Target</p>
                  <p className="font-semibold text-[#4A154B]">{promo.target_tiers.includes('all') ? 'All Tiers' : promo.target_tiers.join(', ')}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                <Calendar className="w-4 h-4" />
                {format(new Date(promo.start_date), 'MMM d')} - {format(new Date(promo.end_date), 'MMM d, yyyy')}
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-[#F5E6D3]">
                <Button variant="ghost" size="sm" onClick={() => openModal(promo)} data-testid={`edit-promo-${promo.id}`}>
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(promo)} className="text-red-500 hover:text-red-700" data-testid={`delete-promo-${promo.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#4A154B]">
              {editing ? 'Edit Promotion' : 'Add Promotion'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="form-label">Name *</label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Weekend Special" data-testid="promo-name-input" />
            </div>
            <div>
              <label className="form-label">Description</label>
              <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Promotion description" rows={2} data-testid="promo-description-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Type *</label>
                <Select value={form.promotion_type} onValueChange={(v) => setForm({...form, promotion_type: v})}>
                  <SelectTrigger data-testid="promo-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {promoTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="form-label">Value *</label>
                <Input type="number" value={form.value} onChange={(e) => setForm({...form, value: e.target.value})} placeholder={form.promotion_type === 'multiplier' ? '2' : '50'} min="0" step="0.1" data-testid="promo-value-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Start Date *</label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} data-testid="promo-start-input" />
              </div>
              <div>
                <label className="form-label">End Date *</label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} data-testid="promo-end-input" />
              </div>
            </div>
            <div>
              <label className="form-label">Target Tiers</label>
              <div className="flex flex-wrap gap-3 mt-2">
                {tiers.map(tier => (
                  <label key={tier} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={form.target_tiers.includes(tier)} onCheckedChange={() => handleTierToggle(tier)} />
                    <span className="text-sm capitalize">{tier}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(c) => setForm({...form, is_active: c})} data-testid="promo-active-switch" />
              <span className="text-sm">Active</span>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="btn-primary" data-testid="save-promo-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editing ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPromotions;
